import { useContext, useEffect, useState } from "react";
import { RxPlusCircled } from "react-icons/rx";
import { AccountContext } from "../../../contexts/AccountContexts";
import { loadingActive, pageLoadingActive } from "../../../components/Background";
import { CiEdit } from "react-icons/ci";
import { requestDeleteFaq, requestEditFaq, requestFaqs, requestSubmitNewFaq } from "../../../requests/setup_requests";
import { ContentContext } from "../../../contexts/ContentContext";
import { PageHeader } from "../../../components/HeaderComponents";
import { useConfirm } from "../../../contexts/ModalContexts";
import { useTranslation } from "react-i18next";
import { PermissionContext } from "../../../contexts/PermissionContexts";
import { useNavigate } from "react-router-dom";
import { darkAndLightThemeDefault } from "../../../components/ThemeComponents";
import { languageOptions } from "../../../entries";


export const FAQSetup = () => {
    const { t } = useTranslation('settings')
    const { openErrorModal } = useContext(ContentContext)
    const { hasPermission } = useContext(PermissionContext)

    const [openNew, setOpenNew] = useState(false)
    const [faqs, setFaqs] = useState([]);
    const [editInProgress, setEditInProgress] = useState(false);

    const [inputKey, setInputKey] = useState(Date.now());


    const navigator = useNavigate()

    const handleOpenNew = () => {
        setOpenNew(true)
    }

    useEffect(() => {

        if (!hasPermission("faq:view")) {
            navigator("/admin/dashboard")
            return
        }

        pageLoadingActive(true)
        requestFaqs({})
            .then((data) => {
                setFaqs(data.faqs)
                pageLoadingActive(false)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
                pageLoadingActive(false)
            })
    }, [])

    useEffect(() => {
        if (!editInProgress) {
            setInputKey(Date.now());
        }
    }, [editInProgress])

    useEffect(() => {
        if (openNew) {
            setEditInProgress(true);
        } else {
            setEditInProgress(false);
        }
    }, [openNew]);

    if (!hasPermission("faq:view")) {
        return <></>
    }

    return (
        <>
            <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600 rounded-md px-4 py-3 gap-3 min-h-[500px]">
                <PageHeader pageTitle={t("FAQ Setup")} />
                <span>{t('Set up the frequently asked questions.')}</span>
                <div className="grid grid-cols-4 w-full gap-3 items-center">
                    <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                        {t('FAQ')}
                    </span>
                    <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                        {t('FAQ (English)')}
                    </span>
                    <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                        {t('Value')}
                    </span>
                </div>
                {
                    faqs.map((faq, index) =>
                        <FAQElement
                            key={faq.id}
                            props={faq}
                            setFaqs={setFaqs}
                            editInProgress={editInProgress}
                            setEditInProgress={setEditInProgress}
                        />
                    )
                }

                {
                    !openNew ?
                        (
                            hasPermission("faq:create") &&
                            <div className="flex w-full justify-center mt-4">
                                <button className="btn w-full btn-md h-9 p-0 text-primary dark:text-blue-600 bg-white dark:bg-gray-700 hover:text-white hover:bg-primary  border-gray-500 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-500"
                                    disabled={editInProgress}
                                    onClick={handleOpenNew}
                                >
                                    <RxPlusCircled size={30} />
                                </button>
                            </div>
                        )
                        :
                        <FAQElementEdit
                            key={"$$$$$$$$$"}
                            props
                            setOpen={setOpenNew}
                            setElement={setFaqs}
                            edit={false}
                            placeholder="FAQ"
                        />
                }
            </div>
        </>
    )
}


const FAQElement = ({ props, setFaqs, editInProgress, setEditInProgress }) => {
    const { hasPermission } = useContext(PermissionContext)

    const [edit, setEdit] = useState(false);
    const [newElements, setNewElements] = useState(null);

    const handleOpenEdit = () => {
        setEdit(true);
    };

    useEffect(() => {
        if (edit) {
            setEditInProgress(true);
        } else {
            setEditInProgress(false);
        }
    }, [edit]);

    useEffect(() => {
        if (newElements !== null) {
            setFaqs(newElements);
            setNewElements(null);
        }
    }, [newElements]);


    return !edit ? (
        <div className="flex flex-col w-full">

            <div className={`grid grid-cols-4 w-full gap-5 items-center group hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 p-1 py-2 border rounded-lg ${editInProgress ? "bg-gray-100 dark:bg-gray-600" : ""}`}>
                <div className="col-span-2 group grid grid-cols-2 gap-5">

                    <div className="flex flex-col gap-2">
                        <span className="font-semibold">{props.title}</span>
                        <span className="h-10 overflow-clip text-wrap whitespace-break-spaces group-hover:h-40 group-hover:overflow-auto max-h-max transition-all duration-200">{props.message}</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <span className="font-semibold">{props.engTitle}</span>
                        <span className="h-10 overflow-clip text-wrap whitespace-break-spaces group-hover:h-40 group-hover:overflow-auto max-h-max transition-all duration-200">{props.engMessage}</span>
                    </div>
                </div>
                <span>{props.value}</span>
                {
                    hasPermission("faq:edit") &&
                    <button
                        className="btn btn-sm w-8 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:invisible ease-in-out border-none bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white p-0"
                        disabled={editInProgress}
                        onClick={handleOpenEdit}
                    >
                        <CiEdit size={24} />
                    </button>
                }
            </div>
        </div>
    ) : (
        <FAQElementEdit
            key={props.id}
            props={props}
            setOpen={setEdit}
            setElement={setNewElements}
            edit={true}
            placeholder={"FAQ"}
        />
    );
}


const FAQElementEdit = ({ props, setOpen, setElement, edit, placeholder }) => {
    const { t } = useTranslation('settings')
    const { user } = useContext(AccountContext);
    const { openErrorModal } = useContext(ContentContext)
    const getConfirmation = useConfirm()

    const [title, setTitle] = useState(props.title ? props.title : "");
    const [engTitle, setEngTitle] = useState(props.engTitle ? props.engTitle : "");
    const [message, setMessage] = useState(props.message ? props.message : "");
    const [engMessage, setEngMessage] = useState(props.engMessage ? props.engMessage : "");
    const [value, setValue] = useState(props.value ? props.value : 0);

    const handleSubmit = () => {
        loadingActive(true);
        if (!edit) {
            requestSubmitNewFaq({
                title: title,
                engTitle: engTitle,
                message: message,
                engMessage: engMessage,
                value: value
            })
                .then((data) => {
                    setElement(data.faqs);
                    setOpen(false);
                    loadingActive(false);
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false)

                });
        } else {
            requestEditFaq({
                id: props.id,
                title: title,
                engTitle: engTitle,
                message: message,
                engMessage: engMessage,
                value: value
            })
                .then((data) => {
                    setElement(data.faqs);
                    setOpen(false);
                    loadingActive(false);
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false)

                });
        }
    }

    const handleDelete = async () => {
        const isConfirmed = await getConfirmation(t("Are you sure you want to delete this item? This action cannot be undone."))
        if (isConfirmed) {
            loadingActive(true);
            requestDeleteFaq({ id: props.id }).then((data) => {
                setOpen((prev) => false);
                setElement(data.faqs);
                loadingActive(false);
            })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false)
                });
        }
    }


    const handleClose = () => {
        setOpen(false);
    }

    const submitCondition = message === "" || engMessage === "" ||
        title == "" || engTitle == "" ||
        (title === props.title && message === props.message && value === props.value && engTitle === props.engTitle && engMessage === props.engMessage);

    return (
        <div className="flex flex-col gap-2 border rounded-lg p-1">
            <div className="grid grid-cols-3 w-full gap-3 items-center p-1 py-2">
                <div className="flex flex-col gap-2">
                    <label className="form-control">
                        <input
                            type="text"
                            placeholder={t('Title')}
                            className="input input-bordered input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                            required={true}
                            value={title}
                            onChange={(e) => {
                                setTitle(e.target.value);
                            }}
                        />
                    </label>
                    <label className="form-control">
                        <textarea
                            placeholder={t('Message')}
                            className="textarea textarea-bordered textarea-md w-full h-40 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                            required={true}
                            value={message}
                            onChange={(e) => {
                                setMessage(e.target.value);
                            }}
                        />
                    </label>
                </div>
                <label className="form-control self-start">

                    <input
                        type="number"
                        placeholder={t('Value')}
                        data-theme="light"
                        className="input input-bordered  input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                        required={true}
                        value={value}
                        onChange={(e) => {
                            setValue(e.target.value);
                        }}
                    />
                </label>
                <div className="flex gap-3 mt-auto ltr:ml-auto rtl:mr-auto">
                    <button
                        className="btn bg-green-600 hover:bg-green-600 border-none text-white"
                        onClick={handleSubmit}
                        disabled={submitCondition}
                    >
                        {t('Submit')}
                    </button>
                    {edit && (
                        <button className="btn bg-red-600 hover:bg-red-600 border-none text-white" onClick={handleDelete}>
                            {t('Delete')}
                        </button>
                    )}
                    <button className={`btn ${darkAndLightThemeDefault}`} onClick={handleClose}>
                        {t('Cancel')}
                    </button>
                </div>
            </div>

            <span className="flex gap-1 items-center ltr:pl-1 rtl:pr-1">{<img src={languageOptions["en"].icon} className="w-6" />} {t('English version')}</span>

            <div className="grid grid-cols-3 w-full gap-3 items-center px-1 pb-2 pt-0">
                <div className="flex flex-col gap-2">
                    <label className="form-control">
                        <input
                            type="text"
                            placeholder={'Title'}
                            className="input input-bordered input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                            required={true}
                            value={engTitle}
                            onChange={(e) => {
                                setEngTitle(e.target.value);
                            }}
                        />
                    </label>
                    <label className="form-control">
                        <textarea
                            placeholder={'Message'}
                            className="textarea textarea-bordered textarea-md w-full h-40 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                            required={true}
                            value={engMessage}
                            onChange={(e) => {
                                setEngMessage(e.target.value);
                            }}
                        />
                    </label>
                </div>
                <div className="flex flex-col gap-1 items-start self-start">
                    <span>{t('Please translate the above FAQ to English here.')}</span>
                    <span>{t('International users will see this message instead of the above.')}</span>
                </div>

            </div>
        </div>
    )
}